/*
 * Form Validation
 *
 * This class is build around the native validityState. Based on the validity state,
 * custom messages are shown.
 * Each field will be validated after blur.
 * Once a validation is done, it will keep revalidating on each input change.
 * The form will validate all fields on submit.
 *
 * Messages are fetched from the data-validation attributes on the error element
 * within each field (see form-field mustache template).
 *
*/

class FormValidation {
    constructor(element, {useAjax = false} = {}) {
        this._element = element;
        this._options = {
            useAjax
        };
        this.init();
    }

    init() {
        this.validations = [];
        this.fields = this._element.querySelectorAll('.c-form-field');

        // on formsubmit, validate all fields
        this._element.addEventListener('submit', async (event) => {
            this.validations.forEach(validation => validation?.());
            const isValid = this._element.checkValidity();
            if (!isValid || this._options.useAjax) {
                event.preventDefault();
            }
            if (isValid && this._options.useAjax) {
                // do ajax call
                await ajaxSubmit(this._element, this.fields);
            }
            return isValid;
        });

        // generate validation function and message per form field
        this.fields.forEach((field) => {
            if (field.querySelector('.c-input-date')) {
                this.validations.push(createDateValidator(field));
                return;
            };
            this.validations.push(createValidator(field));
        });
    }
}

async function ajaxSubmit(form, fields) {
    try {
        form.classList.add("loading");
        const formData = new FormData(form);
        const response = await fetch(form.action, {
            method: "POST",
            body: formData,
            credentials: "same-origin",
            headers: {
                "X-Requested-With": "XMLHttpRequest",
            },
        });
        const contentType = response.headers.get("content-type");
        form.classList.remove("loading");
        let result = false
        if (contentType?.includes("application/json")) {
            result = await response.json();
            if (!response.ok && result) {
                fields?.forEach?.((field) => {
                    const input = field.querySelector(':is(input, select, textarea)[name]');
                    let message = result[input.name] || result[input.id];
                    if (message) {
                        const label = field.querySelector('label');
                        const errorBox = field.querySelector('.c-form-field__error');
                        const errorText = errorBox.querySelector('.c-form-field__error-message');
                        errorText.textContent = message.replace('{0}', label.textContent);;
                        errorBox.classList.add('active');
                    }
                });
            }  else {
                const redirectUrl = result?.redirectUrl;
                if (redirectUrl) {
                    window.location.href = redirectUrl;
                }
            }
            return result;
        }
    } catch (error) {
        form.classList.remove("loading");
        console.log(error);
        return;
    }
}

/*
 * Create validator for a field.
 * Sets up listeners for blur/input and sets a message if validation fails.
 */
function createValidator(field) {
    const inputs = [...field.querySelectorAll("input:not([type='submit']), select, textarea")];
    if (!inputs.length) {
        return;
    }

    const label = field.querySelector('label');
    const errorBox = field.querySelector('.c-form-field__error');
    const errorText = errorBox.querySelector('.c-form-field__error-message');

    const validationFn = (isBlurred) => {
        if (inputs.every(input => input.validity.valid)) {
            errorBox.classList.remove('active');
            if (inputs.some((input => input.value && isBlurred))) {
                field.classList.add('valid');
            }

            return;
        }
        field.classList.remove('valid');
        errorBox.classList.add('active');
        inputs.some(input => {
            for (let state in input.validity) {
                if (state === 'valid') {
                    continue;
                }
                if (input.validity[state]) {
                    errorText.textContent = setMessage(state, errorBox, label);
                    return true;
                }
            }
            return false;
        });
    }

    inputs.forEach(
        input => {
            let isBlurred = false;
            input.addEventListener('input', () => isBlurred && validationFn(isBlurred));
            input.addEventListener('blur', () => {
                isBlurred = true;
                validationFn(isBlurred);
            });
            input.validate = () => validationFn(true);
        }
    );

    return validationFn;
}

/*
 * Create validator for a date field.
 * Sets up listeners for blur/input and sets a message if validation fails.
 * Is custom because date field consists of three parts (day, month, year) and is done different.
 * Each field can be actively validated on own restrictions, but the date as a whole will also be checked.
 */
function createDateValidator(field) {
    const minDate = new Date(field.querySelector('.c-input-date')?.dataset['min']);
    const maxDate = new Date(field.querySelector('.c-input-date')?.dataset['max']);
    const inputs = {
        Day: field.querySelector('.c-input-date__day input'),
        Month: field.querySelector('.c-input-date__month input'),
        Year: field.querySelector('.c-input-date__year input')
    };
    const label = field.querySelector('label');
    const errorBox = field.querySelector('.c-form-field__error');
    const errorText = errorBox.querySelector('.c-form-field__error-message');

    const validationFn = (isBlurred) => {
        let entriesValid = Object.entries(inputs).every(([scope,input]) => input.validity.valid);
        if (entriesValid) {
            errorBox.classList.remove('active');
            if (Object.entries(inputs).some(([scope, input]) => input.value && isBlurred)) {
                field.classList.add('valid');
            }
        }

        let invalid = Object.entries(inputs).some(([scope, input]) => {
            for (let state in input.validity) {
                if (state === 'valid') {
                    continue;
                }
                if (input.validity[state]){
                    // single field day/month/year is filled incorrectly
                    // always use badInput message from whole date field in this case
                    errorText.textContent = setMessage('BadInput', errorBox, label);
                    return true;
                }
            }
        });

        let year = parseInt(inputs.Year.value, 10);
        let month = parseInt(inputs.Month.value, 10) - 1;
        let day = parseInt(inputs.Day.value, 10);

        // Check if date is filled at all
        if (Number.isInteger(year) || Number.isInteger(month) || Number.isInteger(day)) {
            let date = new Date(year, month, day);
            if (isNaN(date.valueOf()) || inputs.Year.value.length < 4) {
                errorText.textContent = setMessage('BadInput', errorBox, label);
                invalid = true;
            } else if (maxDate && date.getTime() > maxDate.getTime()) {
                errorText.textContent = setMessage('RangeOverflow', errorBox, label);
                invalid = true;
            } else if (minDate && date.getTime() < minDate.getTime()) {
                errorText.textContent = setMessage('RangeUnderflow', errorBox, label);
                invalid = true;
            }
        }

        if (invalid) {
            field.classList.remove('valid');
            errorBox.classList.add('active');
        } else {
            if (Object.values(inputs).some(input => input.value)) {
                field.classList.add('valid');
            }
            errorBox.classList.remove('active');
        }
    }

    let isBlurred = false;

    Object.entries(inputs).forEach(([scope, input]) => {
        input.addEventListener('input', () => isBlurred && validationFn(isBlurred));
        input.addEventListener('blur', () => {
            if (scope === 'Year') {
                isBlurred = true;
            }

            validationFn(isBlurred);
        });
    });

    return validationFn;
}

function setMessage(state, errorBox, label, prefix = '') {
    // grab text from matching data property.
    const propName = prefix + state.charAt(0).toUpperCase() + state.slice(1);
    const message = errorBox.dataset[`msg${propName}`] || 'Validatiefout';

    // Insert label text dynamically.
    return message.replace('{field}', label.textContent);
}

export default FormValidation;